// Loading.js
import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { LeftSide } from '../../components/LeftSide/LeftSide';
import { Navi } from '../../components/Navi/Navi';
import { RightBar } from '../../components/RightBar/RightBar';
import { HotTable } from '@handsontable/react-wrapper';
import { registerAllModules } from 'handsontable/registry';
import 'handsontable/styles/handsontable.css';
import 'handsontable/styles/ht-theme-main.css';
import './FindMid.css';
import AuthService from '../../services/authService';
import { Modal, Button } from 'react-bootstrap';

registerAllModules();

function isValidUrl(url) {
    try {
        new URL(url);
        return true;
    } catch {
        return false;
    }
}

function extractProductId(url) {
    try {
        const match = url.match(/\/products\/(\d+)/);
        return match ? match[1] : null;
    } catch {
        return null;
    }
}

const FindMid = ({ title = 'MID 조회' }) => {
    const [loading, setLoading] = useState(false);
    const hotRef = useRef(null);
    const [tableData, setTableData] = useState([
        ...Array.from({ length: 100 }, () => Array(8).fill('')),
    ]);

    const [showHistoryModal, setShowHistoryModal] = useState(false);

    const [historyData, setHistoryData] = useState([]);
    const [historyLoaded, setHistoryLoaded] = useState(false);
    const PAGE_SIZE = 10;
    const [offset, setOffset] = useState(0);
    const [hasMore, setHasMore] = useState(true);

    const handleMidSearch = async () => {
        const currentData = [...tableData];
        const hot = hotRef.current.hotInstance;

        const hasValidUrl = currentData.some(row => {
            const url = row[0]?.trim(); // 공백 제거
            return url && url.startsWith('http') && isValidUrl(url);
        });

        if (!hasValidUrl) {
            alert('상품 URL을 1개 이상 입력하세요. (http 포함 필수)');
            return;
        }

        for (let i = 0; i < currentData.length; i++) {
            const url = currentData[i][0];
            const status = currentData[i][7];

            // 이미 성공한 행은 건너뜀
            if (!url || !isValidUrl(url) || status?.includes('✅')) continue;

            const productId = extractProductId(url);
            if (!productId) {
                currentData[i][7] = '❌ 유효하지 않은 URL';
                setTableData([...currentData]);
                continue;
            }

            // 스피너 표시
            currentData[i][7] = '<div class="loading-spinner"></div>';
            setTableData([...currentData]);

            hot.setCellMeta(i, 7, 'className', 'processing-row');
            hot.render();

            try {
                const response = await AuthService.authAxios.post(`/product/findmid`, {
                    productUrls: [url],
                });

                const result = response.data;

                if (result.success && result.data?.[0]?.success) {
                    const productData = result.data[0];
                    currentData[i][1] = productData.successItems.syncNvMid || '';
                    currentData[i][2] = productData.isPriceComparison
                        ? productData.groupItems.parentId
                        : '';
                    currentData[i][3] = productData.successItems.product_name || '';
                    currentData[i][4] = productData.successItems.channel_name || '';
                    currentData[i][5] = (productData.successItems.tags || []).join(', ');
                    currentData[i][6] = productData.successItems.price?.toLocaleString() || '';
                    currentData[i][7] = '✅ 성공';
                    hot.setCellMeta(i, 7, 'className', 'completed-row');
                } else {
                    currentData[i][7] = `❌ ${result.data[0].error}`;
                    hot.setCellMeta(i, 7, 'className', 'error-row');
                }

                setTableData([...currentData]);
                hot.render();
            } catch (e) {
                alert(e);
                currentData[i][7] = '❌ 네트워크 오류';
                hot.setCellMeta(i, 7, 'className', 'error-row');
                setTableData([...currentData]);
                hot.render();
            }

            await new Promise(resolve => setTimeout(resolve, 300));
        }
    };

    const handleReset = () => {
        setTableData([...Array.from({ length: 100 }, () => Array(8).fill(''))]);
    };

    const handleSubmit = e => {
        e.preventDefault();
        handleMidSearch();
    };

    const handleDelete = async id => {
        if (!window.confirm('정말 삭제하시겠습니까?')) return;
        try {
            await AuthService.authAxios.post('/log/delete', { id }); // 삭제용 API
            setHistoryData(prev => prev.filter(item => item.id !== id));
            //fetchHistory(); // 다시 불러오기
        } catch (err) {
            console.error('삭제 실패:', err);
        }
    };

    const handleApplyInput = inputString => {
        const values = Object.fromEntries(inputString.split('|').map(pair => pair.split('=')));
        // setKeyword(values.keyword || '');
        // setProductId(values.productId || '');
        setShowHistoryModal(false); // react-bootstrap 모달 닫기
    };

    const fetchHistory = async (nextOffset = 0, append = false) => {
        try {
            const res = await AuthService.authAxios.get('/log/history', {
                params: {
                    gubun: 'findmid',
                    offset: nextOffset,
                },
            });

            if (res.data.success) {
                const newData = res.data.data;
                if (!append) {
                    setHasMore(true); // ✅ 새로 로딩할 땐 초기화
                }
                if (newData.length < PAGE_SIZE) setHasMore(false);

                if (append) {
                    setHistoryData(prev => [...prev, ...newData]);
                    setOffset(prev => prev + PAGE_SIZE);
                } else {
                    setHistoryData(newData);
                    setOffset(PAGE_SIZE);
                }
            }
        } catch (err) {
            console.error('조회 기록 불러오기 실패:', err);
        } finally {
            setHistoryLoaded(true);
        }
    };

    return (
        <>
            {/* <!-- Begin page --> */}
            <div id="wrapper">
                <Navi title={title} />

                <LeftSide />

                <div className="content-page">
                    <div className="content">
                        {/* <!-- Start Content--> */}
                        <div className="container-fluid">
                            <div className="row">
                                <div className="col-xl-12">
                                    <div className="card position-relative">
                                        <button
                                            type="button"
                                            className="btn btn-soft-primary waves-effect waves-light position-absolute"
                                            style={{ top: '16px', right: '16px', zIndex: 10 }}
                                            onClick={() => {
                                                setShowHistoryModal(true);
                                                fetchHistory();
                                            }}
                                        >
                                            <i className="mdi mdi-history me-1"></i>
                                            조회 기록
                                        </button>

                                        <form onSubmit={handleSubmit}>
                                            <div className="card-body">
                                                <h4 className="card-title">✔️ 안내사항</h4>
                                                <p>
                                                    ▪️ 상품 페이지 URL을 https부터 입력하거나
                                                    붙여넣으세요.
                                                    <br />
                                                    &nbsp;&nbsp;입력 예시:
                                                    https://smartstore.naver.com/tsad/products/12345678901
                                                    <br />
                                                    ▪️ 실패한 항목이 있으면, 다시 조회 버튼을 누르면
                                                    해당 항목만 재조회합니다.
                                                    <br />
                                                    ▪️ 네이버 상품 URL만 조회 가능합니다.
                                                </p>
                                                <div className="button-list mb-2 text-center">
                                                    <button
                                                        type="submit"
                                                        className="btn btn-primary waves-effect waves-light"
                                                    >
                                                        {loading && (
                                                            <span
                                                                className="spinner-border spinner-border-sm me-1"
                                                                role="status"
                                                                aria-hidden="true"
                                                            ></span>
                                                        )}
                                                        조회
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="btn btn-secondary waves-effect waves-light"
                                                        onClick={handleReset}
                                                    >
                                                        초기화
                                                    </button>
                                                </div>

                                                <div
                                                    className="ht-theme-main-dark-auto"
                                                    style={{ height: '70vh', width: '100%' }}
                                                >
                                                    <HotTable
                                                        ref={hotRef}
                                                        data={tableData}
                                                        colHeaders={[
                                                            '상품 URL <span class="text-danger">*</span>',
                                                            '단일 MID',
                                                            '원부 MID',
                                                            '상품명',
                                                            '스토어명',
                                                            '태그',
                                                            '가격',
                                                            '상태',
                                                        ]}
                                                        contextMenu={{
                                                            items: {
                                                                undo: {
                                                                    name: '되돌리기',
                                                                },
                                                                redo: {
                                                                    name: '다시 실행하기',
                                                                },
                                                                copy: {
                                                                    name: '복사',
                                                                },
                                                                cut: {
                                                                    name: '잘라내기',
                                                                },
                                                            },
                                                        }}
                                                        rowHeaders={true}
                                                        autoWrapRow={true}
                                                        autoWrapCol={true}
                                                        stretchH="all"
                                                        height="100%"
                                                        width="100%"
                                                        colWidths={[
                                                            300, 100, 100, 200, 100, 200, 80, 100,
                                                        ]}
                                                        cells={(row, col) => {
                                                            const zebraClass =
                                                                row % 2 === 0
                                                                    ? 'row-even'
                                                                    : 'row-odd';

                                                            if (col === 0) {
                                                                return {
                                                                    readOnly: false,
                                                                    className: `input-url-cell htWrap htLeft htMiddle ${zebraClass}`,
                                                                };
                                                            }

                                                            return {
                                                                readOnly: true,
                                                                className: `readonly-cell htWrap htMiddle ${zebraClass}`,
                                                                renderer:
                                                                    col === 7 ? 'html' : undefined,
                                                            };
                                                        }}
                                                        beforeChange={(changes, source) => {
                                                            if (!changes) return;

                                                            // 붙여넣기 또는 기타 변경으로 인해 100개 초과될 가능성 있을 때 차단
                                                            const isPaste =
                                                                source === 'CopyPaste.paste';
                                                            if (isPaste) {
                                                                const rowCount = tableData.length;
                                                                const maxRowTouched = Math.max(
                                                                    ...changes.map(([row]) => row)
                                                                );
                                                                const predictedRowCount =
                                                                    maxRowTouched + 1;

                                                                if (predictedRowCount > 100) {
                                                                    alert(
                                                                        '⚠️ 최대 100개 행까지만 입력할 수 있습니다.'
                                                                    );
                                                                    return false;
                                                                }
                                                            }
                                                        }}
                                                        licenseKey="non-commercial-and-evaluation"
                                                    />
                                                </div>
                                            </div>
                                        </form>
                                    </div>
                                </div>
                                {/* <!-- end col --> */}
                            </div>
                            {/* <!-- end row --> */}
                        </div>
                        {/* <!-- container-fluid --> */}
                    </div>
                    {/* <!-- content --> */}
                </div>
            </div>

            <RightBar />

            <div className="rightbar-overlay"></div>

            <Modal
                show={showHistoryModal}
                onHide={() => setShowHistoryModal(false)}
                size="lg"
                scrollable
            >
                <Modal.Header closeButton>
                    <Modal.Title>조회 기록</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {!historyLoaded ? null : historyData.length === 0 ? (
                        <p>조회 기록이 없습니다.</p>
                    ) : (
                        <ul className="list-group">
                            {historyData.map((item, index) => (
                                <li key={index} className="list-group-item">
                                    <div className="d-flex justify-content-between align-items-start">
                                        <div>
                                            <div className="mb-1">
                                                {(() => {
                                                    const parts = {};
                                                    item.input.split('|').forEach(pair => {
                                                        const [key, value] = pair.split('=');
                                                        parts[key] = value;
                                                    });
                                                    return (
                                                        <>
                                                            <div className="mb-1">
                                                                스토어명:{' '}
                                                                {parts.channel_name || '-'}
                                                                <br />
                                                                상품 URL: {parts.url || '-'}
                                                                <br />
                                                                상품명: {parts.product_name || '-'}
                                                                <br />
                                                                단일 MID: {parts.syncNvMid || '-'} /
                                                                원부 MID: {parts.parentId || '-'}
                                                                <br />
                                                            </div>
                                                            <small className="text-muted d-block">
                                                                {item.updated_at ||
                                                                    item.searched_at}
                                                            </small>
                                                        </>
                                                    );
                                                })()}
                                            </div>
                                        </div>
                                        <div className="ms-2 d-flex flex-row align-items-center">
                                            {/* <button
                                                className="btn btn-sm btn-outline-primary me-1"
                                                onClick={() => {
                                                    handleApplyInput(item.input);
                                                    setShowHistoryModal(false);
                                                }}
                                            >
                                                적용
                                            </button> */}
                                            <button
                                                className="btn btn-sm btn-outline-danger"
                                                style={{ whiteSpace: 'nowrap' }}
                                                onClick={() => handleDelete(item.id)}
                                            >
                                                삭제
                                            </button>
                                        </div>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                    {hasMore && historyData.length > 0 && (
                        <div className="text-center mt-3">
                            <button
                                className="btn btn-outline-secondary btn-sm"
                                onClick={() => fetchHistory(offset, true)}
                            >
                                더보기
                            </button>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="light" onClick={() => setShowHistoryModal(false)}>
                        닫기
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export { FindMid };
